/**
 * Mob Aggro-Range Overlay Config
 *
 * Styling for the per-mob aggro-distance debug circles drawn by
 * MobRenderer.updateAggroIndicator. This is a DEBUG OVERLAY: it visualizes the
 * radius at which a mob will aggro the player, colored by the mob's current
 * aggro state (idle vs. aggroed).
 *
 * The RADIUS is not configured here and is not a constant. It is the server's
 * enforced per-mob value, read off the wire (`NPC.aggroRange` →
 * mobAggroRangeSource). This file styles the drawing only. Before 2026-07-26
 * the overlay drew GAMEPLAY_CONFIG.mobAI.aggroRange (250px) for every mob,
 * which was correct for 4 of 110 — see src/shared/logic/AggroRange.ts.
 *
 * Three drawing cases, one per AggroRangeReading:
 *   radius  → `idle` / `aggro` circle at the enforced px value
 *   never   → nothing (0 = this mob never aggros by proximity; that IS the truth)
 *   unknown → the `unknown` marker below — deliberately a small fixed-size glyph
 *             that cannot be mistaken for a range, never a default circle.
 *
 * It is OFF by default (`defaultEnabled: false`) and toggled at runtime via
 * the `toggle_aggro_range` dev action (AggroRangeOverlayState) — the 🎯 tick
 * in the NPCs DevTools tab + the Player "Debug" toggle list, alongside the
 * hitbox / velocity / physics overlays.
 *
 * Two-section file (the simple "defaults + dev override" pattern, see
 * mob-ai.config.ts). Nested style objects override wholesale — to tweak a
 * single field in `idle`/`aggro`/`aggroBox`, supply the whole sub-object in
 * the dev override.
 *
 * Colors are numeric hex (0xRRGGBB) to match the IGraphics fill/stroke API
 * the renderer draws through.
 */

export interface AggroRangeCircleStyle {
  /** Range-circle fill color. */
  fillColor: number;
  /** Range-circle fill alpha (kept very low — a wash, not a disc). */
  fillAlpha: number;
  /** Range-circle outline color. */
  strokeColor: number;
  /** Range-circle outline width (px). */
  strokeWidth: number;
  /** Range-circle outline alpha. */
  strokeAlpha: number;
}

export interface AggroRangeBoxStyle {
  /** Outline color of the box drawn around an aggroed mob. */
  color: number;
  /** Box outline width (px). */
  width: number;
  /** Box outline alpha. */
  alpha: number;
  /** Pixels added to the mob's width/height for the box. */
  padPx: number;
}

export interface AggroRangeUnknownStyle {
  /** Marker color — deliberately unlike idle/aggro so it reads as "no data". */
  color: number;
  /** Full width/height (px) of the fixed-size marker. NOT a radius: it never
   *  scales with anything, so it cannot be misread as a distance. */
  sizePx: number;
  /** Marker line width (px). */
  width: number;
  /** Marker alpha. */
  alpha: number;
}

export interface AggroOverlayConfig {
  /** Whether the overlay is on at load. Debug overlay → false. */
  defaultEnabled: boolean;
  /** Idle (not-yet-aggroed) mob range circle — subtle yellow. */
  idle: AggroRangeCircleStyle;
  /** Aggroed (chasing) mob range circle — red. */
  aggro: AggroRangeCircleStyle;
  /** Red box framing an aggroed mob. */
  aggroBox: AggroRangeBoxStyle;
  /** Marker drawn when the server published NO aggro range for this mob. */
  unknown: AggroRangeUnknownStyle;
}

export const AGGRO_OVERLAY_DEFAULTS: AggroOverlayConfig = {
  defaultEnabled: false,
  idle: {
    fillColor: 0xffff00,
    fillAlpha: 0.02,
    strokeColor: 0xffcc00,
    strokeWidth: 1,
    strokeAlpha: 0.15,
  },
  aggro: {
    fillColor: 0xff0000,
    fillAlpha: 0.04,
    strokeColor: 0xff4444,
    strokeWidth: 1,
    strokeAlpha: 0.3,
  },
  aggroBox: {
    color: 0xff0000,
    width: 2,
    alpha: 0.8,
    padPx: 4,
  },
  unknown: {
    color: 0xff00ff,
    sizePx: 14,
    width: 1,
    alpha: 0.9,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through to
 * AGGRO_OVERLAY_DEFAULTS. Example: `{ defaultEnabled: true }` to have the
 * circles on from boot while iterating on aggro tuning.
 */
export const AGGRO_OVERLAY_DEV_OVERRIDE: Partial<AggroOverlayConfig> = {};

export const AGGRO_OVERLAY: AggroOverlayConfig = {
  ...AGGRO_OVERLAY_DEFAULTS,
  ...AGGRO_OVERLAY_DEV_OVERRIDE,
};
